import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiPlus, FiTrash2, FiUpload, FiShield, FiUser, FiEdit2 } from 'react-icons/fi';
import { accessAssignmentAPI, instituteAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import AccessAssignmentModal from './AccessAssignmentModal';

const AccessAssignments = () => {
  const { isSuperAdmin, access } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const instituteUuid = searchParams.get('instituteUuid') || '';

  const [institutes, setInstitutes] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isSuperAdmin() && institutes.length === 0) {
      instituteAPI.getAll().then((res) => setInstitutes(res.data || [])).catch(() => {});
    }
  }, [isSuperAdmin, institutes.length]);

  const scopeParams = instituteUuid ? { instituteUuid } : {};
  const canLoad = !isSuperAdmin() || !!instituteUuid;

  const fetchAssignments = useCallback(async () => {
    if (!canLoad) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await accessAssignmentAPI.getAll(scopeParams);
      setAssignments(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load access assignments');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instituteUuid, canLoad]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleAdd = () => {
    setSelectedAssignment(null);
    setShowModal(true);
  };

  const handleEdit = (assignment) => {
    setSelectedAssignment(assignment);
    setShowModal(true);
  };

  const handleRevoke = async (assignment) => {
    if (!window.confirm(`Revoke access for ${assignment.email}?`)) return;
    try {
      await accessAssignmentAPI.delete(assignment._id, scopeParams);
      fetchAssignments();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to revoke assignment');
    }
  };

  const handleSave = async ({ email, role, campuses }) => {
    const response = selectedAssignment
      ? await accessAssignmentAPI.update(selectedAssignment._id, { role, campuses }, scopeParams)
      : await accessAssignmentAPI.create({ email, role, campuses }, scopeParams);
    if (!response.warning) {
      setShowModal(false);
      setSelectedAssignment(null);
      fetchAssignments();
    }
    return response;
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportSummary(null);
    try {
      const response = await accessAssignmentAPI.bulkImport(file, scopeParams);
      setImportSummary(response.data);
      fetchAssignments();
    } catch (err) {
      setError(err.message || 'Failed to import CSV');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (isSuperAdmin() && !instituteUuid) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Access</h1>
          <p className="text-gray-600 mb-6">Choose a district to manage its admins and counselors.</p>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
            {institutes.map((inst) => (
              <button
                key={inst.uuid}
                onClick={() => setSearchParams({ instituteUuid: inst.uuid })}
                className="w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <span className="font-medium text-gray-900">{inst.name}</span>
                <span className="text-sm text-gray-400">{inst.shortcode}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Access</h1>
            <p className="text-gray-600">
              {access?.instituteName ? `Admins and counselors for ${access.instituteName}` : 'Manage district admins and counselors'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <label className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
              <FiUpload className="h-5 w-5 mr-2" />
              {importing ? 'Importing...' : 'Upload Counselor CSV'}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelected}
                disabled={importing}
                className="hidden"
              />
            </label>
            <button
              onClick={handleAdd}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <FiPlus className="h-5 w-5 mr-2" />
              Add
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
        )}

        {importSummary && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-1">
            <p>
              Processed {importSummary.counselorsProcessed} counselors from {importSummary.totalRows} rows —{' '}
              {importSummary.created} added, {importSummary.updated} updated.
            </p>
            {importSummary.skippedNoEmail > 0 && <p>{importSummary.skippedNoEmail} rows skipped (no email).</p>}
            {importSummary.skippedNoCampus > 0 && <p>{importSummary.skippedNoCampus} rows skipped (no campus).</p>}
            {importSummary.conflicts?.length > 0 && (
              <p className="text-red-700">
                Already assigned to another district, skipped: {importSummary.conflicts.join(', ')}
              </p>
            )}
            {importSummary.unrecognizedCampuses?.length > 0 && (
              <p className="text-yellow-700">
                Not found in this district's roster: {importSummary.unrecognizedCampuses.join(', ')}
              </p>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : assignments.length === 0 ? (
            <div className="p-12 text-center">
              <FiUser className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No admins or counselors assigned yet</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campuses</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {assignments.map((a) => (
                  <tr key={a._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{a.email}</td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          a.role === 'districtAdmin' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'
                        }`}
                      >
                        <FiShield className="h-3 w-3 mr-1" />
                        {a.role === 'districtAdmin' ? 'District Admin' : 'Counselor'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {a.role === 'districtAdmin' ? 'All campuses' : (a.campuses || []).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap space-x-3">
                      {(isSuperAdmin() || a.role !== 'districtAdmin') && (
                        <button onClick={() => handleEdit(a)} className="text-primary-600 hover:text-primary-800">
                          <FiEdit2 className="h-4 w-4 inline" />
                        </button>
                      )}
                      {(isSuperAdmin() || a.role !== 'districtAdmin') && (
                        <button onClick={() => handleRevoke(a)} className="text-red-600 hover:text-red-800">
                          <FiTrash2 className="h-4 w-4 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <AccessAssignmentModal
          assignment={selectedAssignment}
          allowRoleChoice={isSuperAdmin()}
          onClose={() => {
            setShowModal(false);
            setSelectedAssignment(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default AccessAssignments;
